import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { CartItem } from "@/context/CartContext";
import type { Product } from "@/types/product";

export interface RecommendationRule {
  id: string;
  name: string;
  active: boolean;
  priority: number;
  starts_at: string | null;
  ends_at: string | null;
  trigger_type: "product_type" | "category" | "brand" | "product";
  trigger_match: "any" | "all";
  trigger_values: string[];
  title_lv: string | null;
  title_en: string | null;
  description_lv: string | null;
  description_en: string | null;
  bundle_label_lv: string | null;
  bundle_label_en: string | null;
  add_button_text_lv: string | null;
  add_button_text_en: string | null;
  completed_message_lv: string | null;
  completed_message_en: string | null;
  display_product_page: boolean;
  display_cart_drawer: boolean;
  display_cart_page: boolean;
  discount_type: "none" | "percent" | "amount";
  discount_value: number;
  discount_condition: "all_required" | "any";
  max_discounted_qty_per_product: number;
  stack_with_coupons: boolean;
  stack_with_auto: boolean;
}

export interface RuleProduct {
  id: string;
  rule_id: string;
  product_id: string;
  sort_order: number;
  required: boolean;
  default_qty: number;
  max_qty: number;
  product?: Product;
}

export interface EvaluatedRule {
  rule: RecommendationRule;
  recommendedProducts: RuleProduct[];
  triggerMatched: boolean;
  bundleComplete: boolean;
  discountAmount: number;
  discountedItems: Array<{ product_id: string; amount: number; label: string }>;
}

function cartMatchesTrigger(rule: RecommendationRule, items: CartItem[]): boolean {
  if (!rule.trigger_values.length) return false;
  const test = (item: CartItem, val: string): boolean => {
    switch (rule.trigger_type) {
      case "product_type":
        return (item.product as any).product_type === val;
      case "brand":
        return (item.product.brand || "").toLowerCase() === val.toLowerCase();
      case "category":
        return item.product.category_id === val || item.product.categorySlug === val;
      case "product":
        return item.product.id === val;
    }
  };
  if (rule.trigger_match === "all") {
    return rule.trigger_values.every((v) => items.some((i) => test(i, v)));
  }
  return rule.trigger_values.some((v) => items.some((i) => test(i, v)));
}

export function evaluateRule(rule: RecommendationRule, ruleProducts: RuleProduct[], items: CartItem[]): EvaluatedRule {
  const triggerMatched = cartMatchesTrigger(rule, items);
  const inCart = (pid: string) => items.some((i) => i.product.id === pid);

  const required = ruleProducts.filter((rp) => rp.required);
  const bundleComplete =
    triggerMatched &&
    (rule.discount_condition === "all_required"
      ? required.length > 0 && required.every((rp) => inCart(rp.product_id))
      : ruleProducts.some((rp) => inCart(rp.product_id)));

  const discountedItems: EvaluatedRule["discountedItems"] = [];
  let discountAmount = 0;

  if (bundleComplete && rule.discount_type !== "none") {
    for (const rp of ruleProducts) {
      const cartItem = items.find((i) => i.product.id === rp.product_id);
      if (!cartItem) continue;
      const qty = Math.min(cartItem.quantity, rule.max_discounted_qty_per_product);
      const unit = cartItem.variantPrice ?? cartItem.product.price;
      const gross = unit * qty;
      const amt =
        rule.discount_type === "percent"
          ? gross * (Number(rule.discount_value) / 100)
          : Math.min(Number(rule.discount_value), gross);
      if (amt > 0) {
        discountAmount += amt;
        discountedItems.push({ product_id: rp.product_id, amount: amt, label: cartItem.product.name });
      }
    }
    discountAmount = Math.round(discountAmount * 100) / 100;
  }

  return { rule, recommendedProducts: ruleProducts, triggerMatched, bundleComplete, discountAmount, discountedItems };
}

export function useRecommendations(items: CartItem[]) {
  const [rules, setRules] = useState<RecommendationRule[]>([]);
  const [ruleProducts, setRuleProducts] = useState<RuleProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: rs } = await supabase
          .from("recommendation_rules")
          .select("*")
          .eq("active", true)
          .order("priority", { ascending: true });
        if (cancelled) return;
        const list = (rs || []) as any[];
        setRules(list as RecommendationRule[]);
        if (!list.length) { setRuleProducts([]); setLoading(false); return; }

        const ruleIds = list.map((r) => r.id);
        const { data: rps } = await supabase
          .from("recommendation_rule_products")
          .select("*")
          .in("rule_id", ruleIds)
          .or("slot.eq.bundle,slot.is.null")
          .order("sort_order", { ascending: true });
        const rpList = (rps || []) as any[];

        const pids = [...new Set(rpList.map((rp) => rp.product_id))];
        let productsById: Record<string, Product> = {};
        if (pids.length) {
          const { data: prods } = await supabase
            .from("products")
            .select("id, name, slug, description, brand, price, compare_at_price, currency, stock, sku, specs, status, product_type, category_id")
            .in("id", pids)
            .eq("status", "active");
          const { data: imgs } = await supabase
            .from("product_images")
            .select("product_id, url, sort_order")
            .in("product_id", pids)
            .order("sort_order", { ascending: true });
          const imgMap: Record<string, string[]> = {};
          for (const im of (imgs || []) as any[]) {
            (imgMap[im.product_id] ||= []).push(im.url);
          }
          for (const p of (prods || []) as any[]) {
            productsById[p.id] = {
              id: p.id, name: p.name, slug: p.slug,
              description: p.description || "",
              category: "", categorySlug: "",
              category_id: p.category_id,
              brand: p.brand || "", price: Number(p.price),
              compareAtPrice: p.compare_at_price ? Number(p.compare_at_price) : undefined,
              currency: p.currency || "EUR",
              stock: p.stock || 0, sku: p.sku || "",
              specs: p.specs || {}, images: imgMap[p.id] || [],
              status: p.status as any, product_type: p.product_type,
            };
          }
        }

        const merged = rpList
          .map((rp) => ({ ...(rp as RuleProduct), product: productsById[rp.product_id] }))
          .filter((rp) => rp.product);
        if (cancelled) return;
        setRuleProducts(merged);
        setLoading(false);
      } catch (err) {
        console.warn("[useRecommendations] failed", err);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const evaluated = useMemo<EvaluatedRule[]>(() => {
    return rules.map((r) =>
      evaluateRule(r, ruleProducts.filter((rp) => rp.rule_id === r.id), items),
    );
  }, [rules, ruleProducts, items]);

  const bundleDiscount = evaluated.reduce((s, e) => s + e.discountAmount, 0);

  return { evaluated, bundleDiscount, loading };
}
